import { BaseHTMLElement } from '../BaseHTMLElement';
import type { ShapeManager } from '../../core/ShapeManager';
import type { CommandManager } from '../../core/CommandManager';
import type { ViewportManager } from '../../core/ViewportManager';
import type { IShape, ShapeTransform } from '../../core/shapes/interfaces/IShape';
import { MoveShape } from '../../core/commands/MoveShape';
import { RotateShape } from '../../core/commands/RotateShape';
import { SetShapeVisibility } from '../../core/commands/SetShapeVisibility';

export class SidePanel extends BaseHTMLElement {
    private shapeManager: ShapeManager;
    private commandManager: CommandManager;
    private viewportManager: ViewportManager;
    private searchQuery: string = '';
    private isOpen: boolean = false;

    constructor(shapeManager: ShapeManager, commandManager: CommandManager, viewportManager: ViewportManager) {
        super('div', 'side-panel');
        this.shapeManager = shapeManager;
        this.commandManager = commandManager;
        this.viewportManager = viewportManager;
        this.render();
        this.subscribeToEvents();
    }

    private render(): void {
        this.element.innerHTML = `
            <button class="side-panel__peek" title="Toggle panel" aria-label="Toggle items panel">
                <svg class="side-panel__peek-icon side-panel__peek-icon--closed" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <rect x="1" y="1" width="14" height="2.5" rx="1" fill="currentColor"/>
                    <rect x="1" y="6.75" width="14" height="2.5" rx="1" fill="currentColor"/>
                    <rect x="1" y="12.5" width="14" height="2.5" rx="1" fill="currentColor"/>
                </svg>
                <svg class="side-panel__peek-icon side-panel__peek-icon--open" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M10 3L5 8l5 5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </button>

            <div class="side-panel__rail" id="side-panel-rail" role="list" aria-label="Compact item list"></div>

            <div class="side-panel__inner">
                <section class="side-panel__section side-panel__section--items" aria-labelledby="side-panel-items-title">
                    <h2 class="side-panel__title" id="side-panel-items-title">Items</h2>
                    <div class="side-panel__search">
                        <input type="text" class="side-panel__search-input" placeholder="Search shapes…" aria-label="Search shapes">
                        <button class="side-panel__search-clear" aria-label="Clear search" hidden>×</button>
                    </div>
                    <div class="side-panel__list" id="shapes-list" role="list"></div>
                </section>

                <details class="side-panel__section side-panel__section--properties" hidden>
                    <summary class="side-panel__title side-panel__summary" id="side-panel-properties-title">Properties</summary>
                    <div class="props-empty" id="props-empty">
                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none"><circle cx="16" cy="16" r="14" stroke="#ccc" stroke-width="2"/><path d="M16 10v6m0 4h.01" stroke="#ccc" stroke-width="2" stroke-linecap="round"/></svg>
                        <span>Select a shape to edit its properties</span>
                    </div>
                    <div class="props-form" id="props-form" hidden></div>
                </details>
            </div>
        `;
        this.bindSearchEvents();
        this.bindPeekButton();
        this.bindRailEvents();
        this.renderList();
        this.renderProperties();
        this.renderRail();
    }

    private subscribeToEvents(): void {
        this.shapeManager.on('shapeAdded', () => {
            this.renderList();
            this.renderRail();
        });
        this.shapeManager.on('shapeRemoved', () => {
            this.renderList();
            this.renderProperties();
            this.renderRail();
        });
        this.shapeManager.on('shapeSelected', (shape: IShape) => {
            this.renderList();
            this.renderProperties(shape);
            this.renderRail();
        });
        this.shapeManager.on('shapeDeselected', () => {
            this.renderList();
            this.renderProperties();
            this.renderRail();
        });
        this.shapeManager.on('selectionChanged', () => {
            this.renderList();
            this.renderProperties(this.shapeManager.getSelectedShape() ?? undefined);
            this.renderRail();
        });
        this.startRealTimeLoop();
    }

    private startRealTimeLoop(): void {
        const loop = () => {
            const shape = this.shapeManager.getSelectedShape();
            if (shape) this.updateTransformFields(shape);
            requestAnimationFrame(loop);
        };
        requestAnimationFrame(loop);
    }

    private updateTransformFields(shape: IShape): void {
        const form = this.element.querySelector<HTMLElement>('#props-form');
        if (!form || form.hidden) return;
        const t = shape.getTransform();
        const rotDeg = Math.round(((t.rotation ?? 0) * 180) / Math.PI);
        const setVal = (prop: string, val: number) => {
            const el = form.querySelector<HTMLInputElement>(`[data-prop="${prop}"]`);
            if (el && document.activeElement !== el) el.value = String(val);
        };
        setVal('x', Math.round(t.x));
        setVal('y', Math.round(t.y));
        setVal('width', Math.round(t.width));
        setVal('height', Math.round(t.height));
        setVal('rotation', rotDeg);
    }

    private bindSearchEvents(): void {
        const input = this.element.querySelector<HTMLInputElement>('.side-panel__search-input')!;
        const clearBtn = this.element.querySelector<HTMLButtonElement>('.side-panel__search-clear')!;

        input.addEventListener('input', () => {
            this.searchQuery = input.value.trim().toLowerCase();
            clearBtn.hidden = !this.searchQuery;
            this.renderList();
        });

        clearBtn.addEventListener('click', () => {
            input.value = '';
            this.searchQuery = '';
            clearBtn.hidden = true;
            this.renderList();
        });
    }

    private bindPeekButton(): void {
        const btn = this.element.querySelector<HTMLButtonElement>('.side-panel__peek')!;
        btn.addEventListener('click', () => {
            this.isOpen = !this.isOpen;
            this.element.classList.toggle('side-panel--open', this.isOpen);
        });
    }

    private bindRailEvents(): void {
        const rail = this.element.querySelector<HTMLElement>('.side-panel__rail')!;
        rail.addEventListener('click', (event) => {
            if (event.target === rail) {
                this.isOpen = true;
                this.element.classList.add('side-panel--open');
            }
        });
    }

    private renderRail(): void {
        const rail = this.element.querySelector<HTMLElement>('#side-panel-rail');
        if (!rail) return;

        const shapes = this.shapeManager.getShapes();
        const selectedShapes = this.shapeManager.getSelectedShapes();

        if (shapes.length === 0) {
            rail.innerHTML = `<button class="side-panel__rail-empty" type="button">No items</button>`;
            rail.querySelector('.side-panel__rail-empty')?.addEventListener('click', () => {
                this.isOpen = true;
                this.element.classList.add('side-panel--open');
            });
            return;
        }

        rail.innerHTML = '';
        [...shapes].reverse().forEach(shape => {
            const isSelected = selectedShapes.includes(shape);
            const isHidden = !shape.visible;
            const hasLayerConflict = this.getOverlapGroup(shape, shapes).length > 1;
            const item = document.createElement('button');
            item.type = 'button';
            item.className = [
                'side-panel__rail-item',
                isSelected ? 'side-panel__rail-item--selected' : '',
                isHidden ? 'side-panel__rail-item--hidden' : '',
                hasLayerConflict ? 'side-panel__rail-item--overlap' : '',
            ].filter(Boolean).join(' ');
            item.title = shape.name;
            item.setAttribute('aria-label', `Select ${shape.name}`);
            item.setAttribute('role', 'listitem');
            item.innerHTML = `<span class="side-panel__rail-icon">${shape.type === 'image' ? '🖼' : '⬛'}</span>`;
            item.addEventListener('click', (event) => {
                event.stopPropagation();
                this.shapeManager.selectShape(shape);
            });
            item.addEventListener('dblclick', (event) => {
                event.stopPropagation();
                this.centerViewportOnShape(shape);
            });
            rail.appendChild(item);
        });
    }

    private renderList(): void {
        const list = this.element.querySelector<HTMLElement>('#shapes-list');
        if (!list) return;

        const shapes = this.shapeManager.getShapes();
        const selectedShapes = this.shapeManager.getSelectedShapes();

        const filtered = this.searchQuery
            ? shapes.filter(s => s.name.toLowerCase().includes(this.searchQuery))
            : shapes;

        if (filtered.length === 0) {
            list.innerHTML = `<div class="shapes-list-empty">${this.searchQuery ? 'No shapes match your search.' : 'No shapes yet.'}</div>`;
            return;
        }

        list.innerHTML = '';
        // Render in reverse so top of list = topmost shape (last in array = highest z-order)
        [...filtered].reverse().forEach(shape => {
            const isSelected = selectedShapes.includes(shape);
            const overlapGroup = this.getOverlapGroup(shape, shapes);
            const hasLayerConflict = overlapGroup.length > 1;
            const layerIndex = overlapGroup.indexOf(shape);
            const canMoveForward = hasLayerConflict && layerIndex < overlapGroup.length - 1;
            const canMoveBack = hasLayerConflict && layerIndex > 0;
            const row = document.createElement('div');
            row.className = `shape-row${isSelected ? ' shape-row--selected' : ''}`;
            row.setAttribute('role', 'listitem');
            row.dataset['id'] = shape.id;
            row.innerHTML = `
                <button class="shape-row__action shape-row__vis${shape.visible ? '' : ' shape-row__vis--hidden'}" title="${shape.visible ? 'Hide' : 'Show'}" aria-label="${shape.visible ? 'Hide shape' : 'Show shape'}">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        ${shape.visible
                            ? '<ellipse cx="7" cy="7" rx="5" ry="3.5" stroke="currentColor" stroke-width="1.5"/><circle cx="7" cy="7" r="1.5" fill="currentColor"/>'
                            : '<path d="M2 2l10 10M4.5 4.8A4.5 3 0 007 4c2.76 0 5 1.34 5 3-.28.66-.8 1.24-1.5 1.66M2 7c.28-.66.8-1.24 1.5-1.66" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>'
                        }
                    </svg>
                </button>
                <span class="shape-row__icon">${shape.type === 'image' ? '🖼' : '⬛'}</span>
                <span class="shape-row__name" title="${shape.name}">${shape.name}</span>
                <button class="shape-row__action shape-row__rename-btn" title="Rename" aria-label="Rename shape">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M8.7 2.2l2.1 2.1M2.5 9.1L9 2.6a1.5 1.5 0 012.1 2.1L4.6 11.2 2 12l.5-2.9z" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </button>
                ${hasLayerConflict ? `
                    <button class="shape-row__action shape-row__move-forward" title="Move forward" aria-label="Move shape forward" ${canMoveForward ? '' : 'disabled'}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M7 2.5v9M3.5 6L7 2.5 10.5 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                    <button class="shape-row__action shape-row__move-back" title="Move back" aria-label="Move shape back" ${canMoveBack ? '' : 'disabled'}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M7 11.5v-9M3.5 8L7 11.5 10.5 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                ` : ''}
            `;

            row.querySelector('.shape-row__vis')!.addEventListener('click', (e) => {
                e.stopPropagation();
                const cmd = new SetShapeVisibility(shape, !shape.visible);
                this.commandManager.execute(cmd);
                this.renderList();
                this.renderRail();
            });

            row.querySelector('.shape-row__rename-btn')!.addEventListener('click', (e) => {
                e.stopPropagation();
                this.shapeManager.selectShape(shape);
                this.startInlineRename(row, shape);
            });

            row.querySelector('.shape-row__move-forward')?.addEventListener('click', (e) => {
                e.stopPropagation();
                this.shapeManager.selectShape(shape);
                this.moveForwardInOverlapGroup(shape);
                this.renderList();
                this.renderProperties(shape);
                this.renderRail();
            });

            row.querySelector('.shape-row__move-back')?.addEventListener('click', (e) => {
                e.stopPropagation();
                this.shapeManager.selectShape(shape);
                this.moveBackInOverlapGroup(shape);
                this.renderList();
                this.renderProperties(shape);
                this.renderRail();
            });

            row.addEventListener('click', () => {
                this.shapeManager.selectShape(shape);
            });

            row.addEventListener('dblclick', (e) => {
                e.stopPropagation();
                this.centerViewportOnShape(shape);
            });

            list.appendChild(row);
        });
    }

    private getShapeRect(shape: IShape): { left: number; top: number; right: number; bottom: number } {
        const transform = shape.getTransform();
        return {
            left: transform.x,
            top: transform.y,
            right: transform.x + transform.width,
            bottom: transform.y + transform.height,
        };
    }

    private shapesOverlap(a: IShape, b: IShape): boolean {
        const rectA = this.getShapeRect(a);
        const rectB = this.getShapeRect(b);

        return (
            rectA.left < rectB.right &&
            rectA.right > rectB.left &&
            rectA.top < rectB.bottom &&
            rectA.bottom > rectB.top
        );
    }

    private getOverlapGroup(shape: IShape, shapes = this.shapeManager.getShapes()): IShape[] {
        return shapes.filter(candidate => candidate === shape || this.shapesOverlap(shape, candidate));
    }

    private centerViewportOnShape(shape: IShape): void {
        const transform = shape.getTransform();
        this.viewportManager.centerOnWorldPoint(
            transform.x + (transform.width / 2),
            transform.y + (transform.height / 2)
        );
    }

    private moveForwardInOverlapGroup(shape: IShape): void {
        const group = this.getOverlapGroup(shape);
        const currentGroupIndex = group.indexOf(shape);
        const target = group[currentGroupIndex + 1];
        if (!target) return;

        while (this.shapeManager.getShapes().indexOf(shape) <= this.shapeManager.getShapes().indexOf(target)) {
            this.shapeManager.bringForward(shape);
        }
    }

    private moveBackInOverlapGroup(shape: IShape): void {
        const group = this.getOverlapGroup(shape);
        const currentGroupIndex = group.indexOf(shape);
        const target = group[currentGroupIndex - 1];
        if (!target) return;

        while (this.shapeManager.getShapes().indexOf(shape) >= this.shapeManager.getShapes().indexOf(target)) {
            this.shapeManager.sendBack(shape);
        }
    }

    private startInlineRename(row: HTMLElement, shape: IShape): void {
        const nameEl = row.querySelector<HTMLElement>('.shape-row__name')!;
        const oldName = shape.name;
        const input = document.createElement('input');
        input.className = 'shape-row__rename';
        input.value = oldName;
        nameEl.replaceWith(input);
        input.focus();
        input.select();

        const commit = () => {
            const newName = input.value.trim() || oldName;
            shape.name = newName;
            this.renderList();
            this.renderRail();
            if (shape === this.shapeManager.getSelectedShape()) {
                this.renderProperties(shape);
            }
        };

        input.addEventListener('blur', commit, { once: true });
        input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') { e.preventDefault(); input.blur(); }
            if (e.key === 'Escape') { shape.name = oldName; input.blur(); }
        });
    }

    private renderProperties(shape?: IShape): void {
        const panel = this.element.querySelector<HTMLDetailsElement>('.side-panel__section--properties')!;
        const empty = this.element.querySelector<HTMLElement>('#props-empty')!;
        const form = this.element.querySelector<HTMLElement>('#props-form')!;

        if (!panel || !empty || !form) {
            console.warn('[SidePanel] renderProperties: DOM elements not found');
            return;
        }

        const selectedShapes = this.shapeManager.getSelectedShapes();
        if (selectedShapes.length > 1) {
            this.renderMultiProperties(panel, empty, form, selectedShapes);
            return;
        }

        if (!shape) {
            panel.hidden = true;
            panel.open = false;
            empty.hidden = false;
            form.hidden = true;
            return;
        }

        try {
            panel.hidden = false;
            empty.hidden = true;
            form.hidden = false;

            const t = shape.getTransform();
            const rotDeg = Math.round(((t.rotation ?? 0) * 180) / Math.PI);
            const opacity = Math.round(((shape.container as any).alpha ?? 1) * 100);
            const overlapGroup = this.getOverlapGroup(shape);
            const hasLayerConflict = overlapGroup.length > 1;
            const layerIndex = overlapGroup.indexOf(shape);
            const canMoveForward = hasLayerConflict && layerIndex < overlapGroup.length - 1;
            const canMoveBack = hasLayerConflict && layerIndex > 0;

        form.innerHTML = `
            <div class="props-group">
                <div class="props-group__title">Identity</div>
                <div class="props-section">
                    <label class="props-label">Name</label>
                    <input class="props-input" data-prop="name" type="text" value="${this.escapeHtml(shape.name)}">
                </div>
            </div>

            <div class="props-group">
                <div class="props-group__title">Arrange</div>
                <div class="props-section">
                    <label class="props-label">Layer position <span class="props-opacity-val">${hasLayerConflict ? `${layerIndex + 1} of ${overlapGroup.length}` : 'No overlap'}</span></label>
                    <div class="props-zorder">
                        <button class="props-btn" id="btn-bring-fwd" ${canMoveForward ? '' : 'disabled'}>↑ Forward</button>
                        <button class="props-btn" id="btn-send-back" ${canMoveBack ? '' : 'disabled'}>↓ Back</button>
                    </div>
                    ${hasLayerConflict ? '' : '<div class="props-hint">This shape does not overlap anything, so its layer position is not important.</div>'}
                </div>
            </div>

            <details class="props-group props-group--collapsible">
                <summary class="props-group__title props-group__summary">Details</summary>
                <div class="props-subgroup">
                    <div class="props-subgroup__title">Position</div>
                    <div class="props-grid">
                        <div class="props-section">
                            <label class="props-label">X</label>
                            <input class="props-input" data-prop="x" type="number" value="${Math.round(t.x)}">
                        </div>
                        <div class="props-section">
                            <label class="props-label">Y</label>
                            <input class="props-input" data-prop="y" type="number" value="${Math.round(t.y)}">
                        </div>
                        <div class="props-section">
                            <label class="props-label">Width</label>
                            <input class="props-input" data-prop="width" type="number" min="1" value="${Math.round(t.width)}">
                        </div>
                        <div class="props-section">
                            <label class="props-label">Height</label>
                            <input class="props-input" data-prop="height" type="number" min="1" value="${Math.round(t.height)}">
                        </div>
                    </div>
                    <div class="props-section">
                        <label class="props-label">Rotation (°)</label>
                        <input class="props-input" data-prop="rotation" type="number" value="${rotDeg}">
                    </div>
                </div>
                <div class="props-subgroup">
                    <div class="props-subgroup__title">Appearance</div>
                    <div class="props-section">
                        <label class="props-label">Opacity <span class="props-opacity-val props-opacity-value">${opacity}%</span></label>
                        <input class="props-range" data-prop="opacity" type="range" min="0" max="100" value="${opacity}">
                    </div>
                </div>
            </details>
        `;

            this.bindPropertyEvents(form, shape, t);
        } catch (err) {
            console.error('[SidePanel] renderProperties failed:', err);
            form.innerHTML = `<div class="shapes-list-empty" style="color:#ef4444">Error loading properties</div>`;
        }
    }

    private renderMultiProperties(panel: HTMLDetailsElement, empty: HTMLElement, form: HTMLElement, shapes: IShape[]): void {
        panel.hidden = false;
        empty.hidden = true;
        form.hidden = false;
        const transformMode = this.shapeManager.isSelectionTransformMode();
        const avgOpacity = Math.round(
            shapes.reduce((sum, shape) => sum + (((shape.container as any).alpha ?? 1) * 100), 0) / shapes.length
        );

        form.innerHTML = `
            <div class="props-group">
                <div class="props-group__title">Selection</div>
                <div class="props-section">
                    <div class="props-summary">${shapes.length} items selected</div>
                </div>
            </div>
            <div class="props-group">
                <div class="props-group__title">Appearance</div>
                <div class="props-section">
                    <label class="props-label">Opacity <span class="props-opacity-val props-opacity-value">${avgOpacity}%</span></label>
                    <input class="props-range" data-prop="opacity" type="range" min="0" max="100" value="${avgOpacity}">
                </div>
            </div>
            <div class="props-group">
                <div class="props-group__title">Arrange</div>
                <div class="props-section">
                    <div class="props-hint">${transformMode ? 'Transform mode is active. Use the group box to move, resize, or rotate this selection.' : 'Drag any selected item to move the selection. Press T for transform handles.'}</div>
                </div>
            </div>
        `;

        const opacityInput = form.querySelector<HTMLInputElement>('.props-range[data-prop="opacity"]');
        const opacityLabel = form.querySelector<HTMLElement>('.props-opacity-value');
        if (opacityInput && opacityLabel) {
            opacityInput.addEventListener('input', () => {
                const pct = parseInt(opacityInput.value);
                opacityLabel.textContent = `${pct}%`;
                shapes.forEach(shape => {
                    shape.container.alpha = pct / 100;
                });
            });
        }
    }

    private bindPropertyEvents(form: HTMLElement, shape: IShape, originalTransform: ShapeTransform): void {
        const applyInput = (input: HTMLInputElement) => {
            const prop = input.dataset['prop']!;
            const val = input.value;
            const t = shape.getTransform();

            if (prop === 'name') {
                shape.name = val.trim() || shape.name;
                this.renderList();
                this.renderRail();
                return;
            }
            if (prop === 'x' || prop === 'y') {
                const newX = prop === 'x' ? parseFloat(val) : t.x;
                const newY = prop === 'y' ? parseFloat(val) : t.y;
                if (!isNaN(newX) && !isNaN(newY)) {
                    const cmd = new MoveShape(shape, t.x, t.y, newX, newY);
                    this.commandManager.execute(cmd);
                }
            }
            if (prop === 'width' || prop === 'height') {
                const newW = prop === 'width' ? Math.max(1, parseFloat(val)) : t.width;
                const newH = prop === 'height' ? Math.max(1, parseFloat(val)) : t.height;
                if (!isNaN(newW) && !isNaN(newH)) {
                    shape.setSize(newW, newH);
                }
            }
            if (prop === 'rotation') {
                const newRad = (parseFloat(val) * Math.PI) / 180;
                if (!isNaN(newRad)) {
                    const oldRad = t.rotation ?? 0;
                    const cmd = new RotateShape(shape, oldRad, newRad);
                    this.commandManager.execute(cmd);
                }
            }
        };

        form.querySelectorAll<HTMLInputElement>('.props-input').forEach(input => {
            input.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') { e.preventDefault(); applyInput(input); }
            });
            input.addEventListener('blur', () => applyInput(input));
        });

        const opacityInput = form.querySelector<HTMLInputElement>('.props-range[data-prop="opacity"]');
        const opacityLabel = form.querySelector<HTMLElement>('.props-opacity-value');
        if (opacityInput && opacityLabel) {
            opacityInput.addEventListener('input', () => {
                const pct = parseInt(opacityInput.value);
                opacityLabel.textContent = `${pct}%`;
                shape.container.alpha = pct / 100;
            });
        }

        form.querySelector('#btn-bring-fwd')?.addEventListener('click', () => {
            this.moveForwardInOverlapGroup(shape);
            this.renderList();
            this.renderProperties(shape);
        });
        form.querySelector('#btn-send-back')?.addEventListener('click', () => {
            this.moveBackInOverlapGroup(shape);
            this.renderList();
            this.renderProperties(shape);
        });

        // Suppress unused warning
        void originalTransform;
    }

    private escapeHtml(s: string): string {
        return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }
}
